import type {
  BffLogicalDeviceResourceReadApi,
  BffProvisioningConfigurationsMetadataApi,
} from '@ingenico/api-sdk-provisioning-private';
import type { CGInputs } from '@ingenico/react-configuration-generator';
import { TargetEntityTypeEnum } from '@ingenico/react-configuration-generator';
import type { BusinessServiceRequest } from '@ingenico/react-section-utils';
import { useBusinessServiceRequest, useCurrentUser } from '@ingenico/react-section-utils';
import type { DeepPartialBy } from '@ingenico/util-common';
import { toString } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';

export interface UseLogicalDeviceConfigurationTabOutput {
  readonly inputs: DeepPartialBy<
    CGInputs,
    'actions' | 'fgComponents' | 'fgTranslations' | 'isEditEnabled' | 'translations'
  >;
  readonly logicalDeviceQuery: BusinessServiceRequest<BffLogicalDeviceResourceReadApi>;
  readonly isEmptyConfiguration: boolean;
  readonly isConfigurationLoading: boolean;
}

function getSelectedConfigurationId(configurations: BffProvisioningConfigurationsMetadataApi[]): string {
  for (const configurationType of ['Local', 'Assigned', 'Inherited']) {
    const configuration = configurations.find((item) => item.configurationType === configurationType);
    if (configuration) return configuration.configurationId;
  }
  return '';
}

interface UseLogicalDeviceConfigurationTabParams {
  readonly onSave?: () => void;
}

export function useLogicalDeviceConfigurationTab({
  onSave: onSaveProp,
}: UseLogicalDeviceConfigurationTabParams): UseLogicalDeviceConfigurationTabOutput {
  const { logicalDeviceId } = useParams();
  const { dateTimeFormat } = useCurrentUser();

  const logicalDeviceQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService, isRefetch) =>
        deviceManagementService.getLogicalDeviceDetailsBff(toString(logicalDeviceId), {
          isRefetch,
          disableCache: true,
        }),
      [logicalDeviceId],
    ),
  );

  const selectedConfigurationId = useMemo(
    () => getSelectedConfigurationId(logicalDeviceQuery.data?.configurationsMetadata ?? []),
    [logicalDeviceQuery.data?.configurationsMetadata],
  );

  const onSave = useCallback(() => {
    logicalDeviceQuery.refetch();
    onSaveProp?.();
  }, [logicalDeviceQuery, onSaveProp]);

  const inputs: DeepPartialBy<
    CGInputs,
    'actions' | 'fgComponents' | 'fgTranslations' | 'isEditEnabled' | 'translations'
  > = useMemo(
    () => ({
      configurationInput: { entityType: TargetEntityTypeEnum.LOGICAL_DEVICE, id: toString(logicalDeviceId) },
      configurationId: selectedConfigurationId,
      dateTimeFormat,
      actions: {
        onSave,
      },
      isReadOnly: true,
    }),
    [dateTimeFormat, logicalDeviceId, onSave, selectedConfigurationId],
  );

  const isEmptyConfiguration = useMemo(
    () => logicalDeviceQuery.isSuccess && !logicalDeviceQuery.data,
    [logicalDeviceQuery.data, logicalDeviceQuery.isSuccess],
  );

  return {
    inputs,
    logicalDeviceQuery,
    isConfigurationLoading: logicalDeviceQuery.isLoading,
    isEmptyConfiguration,
  };
}
